import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ImagePlus, Plus, Trash2, ArrowUp, ArrowDown, Save, ExternalLink, Images, Package, Leaf, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const OFFERING_META = {
  brand: { heading: "Featured Products", singular: "Product", blurb: "Showcase your product lines with a photo, short description and price.", showHunt: false },
  outfitter: { heading: "Hunt Packages", singular: "Package", blurb: "List your guided hunts — species, duration and pricing.", showHunt: true },
  guide: { heading: "Hunt Packages", singular: "Package", blurb: "List the hunts you guide — species, duration and pricing.", showHunt: true },
  state: { heading: "Resources", singular: "Resource", blurb: "Featured programs and resources.", showHunt: false },
};

const newOffering = () => ({ id: Math.random().toString(36).slice(2, 12), title: "", description: "", image: "", price: "", species: "", duration: "", link: "" });

export default function PartnerProfileEditor() {
  const { isAuthed, ready } = useAuth();
  const { setAuthOpen } = useUI();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(undefined);
  const [partner, setPartner] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [conservation, setConservation] = useState([]);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    if (!ready || !isAuthed) return;
    api.partnerMe().then((d) => {
      if (!d.partner) { setBiz(null); return; }
      const b = d.business || {};
      setBiz(b); setPartner(d.partner);
      setForm({
        name: b.name || "", tagline: b.tagline || "", about: b.about || "", location: b.location || "",
        website: b.website || "", email: b.email || "", phone: b.phone || "",
        cta_label: b.cta_label || "", cta_url: b.cta_url || "",
        states_served: (b.states_served || []).join(", "),
        instagram: b.socials?.instagram || "", facebook: b.socials?.facebook || "", youtube: b.socials?.youtube || "", x: b.socials?.x || "",
      });
      setGallery(b.gallery || []);
      setOfferings(b.offerings || []);
      setConservation(b.conservation || []);
      setResources(b.resources || []);
    }).catch(() => setBiz(null));
  }, [ready, isAuthed]);

  if (!ready || (isAuthed && biz === undefined)) return <Shell><Center><Loader2 className="animate-spin text-forest" /></Center></Shell>;
  if (!isAuthed) return (
    <Shell><Center>
      <div className="text-center" data-testid="editor-signin-gate">
        <h1 className="font-serif text-3xl text-forest">Edit your partner profile</h1>
        <button onClick={() => setAuthOpen(true)} className="mt-5 bg-blaze text-white px-6 py-3 rounded-full font-semibold" data-testid="editor-signin-btn">Sign in</button>
      </div>
    </Center></Shell>
  );
  if (!biz) return (
    <Shell><Center>
      <div className="text-center" data-testid="editor-no-partner">
        <h1 className="font-serif text-3xl text-forest">No partner profile yet</h1>
        <p className="text-charcoal/60 mt-2">Start onboarding to create your HomeRange partner profile.</p>
        <button onClick={() => navigate("/partner/onboarding")} className="mt-5 bg-forest text-bone px-6 py-3 rounded-full font-semibold" data-testid="editor-start-onboarding">Start onboarding</button>
      </div>
    </Center></Shell>
  );

  const meta = OFFERING_META[partner?.partner_type] || OFFERING_META.brand;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadIdentity = async (kind, file) => {
    if (!file) return;
    try {
      const res = kind === "logo" ? await api.uploadBusinessLogo(file) : await api.uploadBusinessCover(file);
      setBiz((b) => ({ ...b, [kind]: res[kind] }));
      toast.success(`${kind === "logo" ? "Logo" : "Cover"} updated`);
    } catch { toast.error("Upload failed"); }
  };

  const addGallery = async (files) => {
    for (const file of Array.from(files || [])) {
      try { const res = await api.partnerUploadGallery(file); setGallery(res.gallery); }
      catch { toast.error("Gallery upload failed"); }
    }
    toast.success("Photos added");
  };
  const removeGallery = async (url) => {
    const next = gallery.filter((g) => g !== url);
    setGallery(next);
    await api.partnerSetup({ gallery: next });
  };
  const moveGallery = async (i, dir) => {
    const next = [...gallery]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setGallery(next);
    await api.partnerSetup({ gallery: next });
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const socials = { instagram: form.instagram, facebook: form.facebook, youtube: form.youtube, x: form.x };
      const res = await api.partnerSetup({
        name: form.name, tagline: form.tagline, about: form.about, location: form.location,
        website: form.website, email: form.email, phone: form.phone,
        cta_label: form.cta_label, cta_url: form.cta_url,
        states_served: form.states_served.split(",").map((s) => s.trim()).filter(Boolean),
        socials, offerings, conservation, resources,
      });
      setPartner(res.partner); setBiz(res.business);
      setOfferings(res.business.offerings || []);
      setConservation(res.business.conservation || []);
      setResources(res.business.resources || []);
      toast.success("Profile saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  const publicPath = partner?.public_path;

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Profile Editor</span>
            <h1 className="font-serif text-3xl text-forest mt-1">Edit your profile</h1>
            <p className="text-sm text-charcoal/60 mt-1">{partner?.completion}% complete · {meta.singular === "Package" ? "Outfitter/Guide" : partner?.partner_type} profile</p>
          </div>
          <div className="flex gap-2">
            {publicPath && <a href={publicPath} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-saddle/25 bg-white px-4 py-2.5 rounded-full text-sm font-semibold text-charcoal/80 hover:border-forest transition-colors" data-testid="editor-view-public"><ExternalLink size={14} /> View</a>}
            <button onClick={saveProfile} disabled={saving} className="inline-flex items-center gap-1.5 bg-forest text-bone px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50 hover:bg-forest-2 transition-colors" data-testid="editor-save-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save changes
            </button>
          </div>
        </div>

        {/* Identity */}
        <Section title="Identity & branding" testid="section-identity">
          <div className="relative rounded-xl overflow-hidden border border-saddle/20 bg-charcoal h-40" data-testid="editor-cover">
            {biz.cover && <img src={biz.cover} alt="cover" className="w-full h-full object-cover" />}
            <label className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer hover:bg-white" data-testid="editor-cover-upload">
              <ImagePlus size={14} /> {biz.cover ? "Change cover" : "Add cover"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadIdentity("cover", e.target.files?.[0])} />
            </label>
            <div className="absolute -bottom-8 left-4 w-20 h-20 rounded-xl bg-white border border-saddle/20 shadow overflow-hidden grid place-items-center">
              {biz.logo ? <img src={biz.logo} alt="logo" className="w-full h-full object-cover" /> : <span className="font-serif text-2xl text-forest">{(form.name || "H")[0]}</span>}
            </div>
          </div>
          <label className="inline-flex items-center gap-1.5 mt-10 text-xs font-semibold text-blaze cursor-pointer" data-testid="editor-logo-upload">
            <ImagePlus size={14} /> {biz.logo ? "Change logo" : "Add logo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadIdentity("logo", e.target.files?.[0])} />
          </label>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <Field label="Name" v={form.name} on={set("name")} testid="edit-name" />
            <Field label="Location" v={form.location} on={set("location")} testid="edit-location" />
          </div>
          <Field className="mt-3" label="Tagline" v={form.tagline} on={set("tagline")} testid="edit-tagline" />
          <TextArea className="mt-3" label="About" v={form.about} on={set("about")} testid="edit-about" />
        </Section>

        {/* Gallery */}
        <Section title="Photo gallery" icon={Images} testid="section-gallery">
          <p className="text-sm text-charcoal/60 -mt-2 mb-3">Show your gear, hunts or scenery. Up to 30 photos.</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="gallery-grid">
            {gallery.map((url, i) => (
              <div key={url} className="relative group aspect-square rounded-lg overflow-hidden border border-saddle/20" data-testid={`gallery-item-${i}`}>
                <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button onClick={() => moveGallery(i, -1)} className="p-1.5 bg-white/90 rounded-full" data-testid={`gallery-left-${i}`}><ArrowUp size={13} className="rotate-[-90deg]" /></button>
                  <button onClick={() => moveGallery(i, 1)} className="p-1.5 bg-white/90 rounded-full" data-testid={`gallery-right-${i}`}><ArrowDown size={13} className="rotate-[-90deg]" /></button>
                  <button onClick={() => removeGallery(url)} className="p-1.5 bg-white/90 rounded-full text-red-600" data-testid={`gallery-remove-${i}`}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
            {gallery.length < 30 && (
              <label className="aspect-square rounded-lg border border-dashed border-saddle/40 grid place-items-center cursor-pointer hover:border-forest transition-colors" data-testid="gallery-upload">
                <div className="text-center text-saddle"><ImagePlus size={20} className="mx-auto" /><span className="text-[11px] font-semibold block mt-1">Add photos</span></div>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addGallery(e.target.files)} />
              </label>
            )}
          </div>
        </Section>

        {/* Offerings */}
        <Section title={meta.heading} icon={Package} testid="section-offerings">
          <p className="text-sm text-charcoal/60 -mt-2 mb-3">{meta.blurb} Remember to <span className="font-semibold text-forest">Save changes</span> when done.</p>
          <div className="space-y-4" data-testid="offerings-list">
            {offerings.map((o, i) => (
              <OfferingCard key={o.id} o={o} i={i} meta={meta}
                onChange={(patch) => setOfferings((arr) => arr.map((x, j) => j === i ? { ...x, ...patch } : x))}
                onRemove={() => setOfferings((arr) => arr.filter((_, j) => j !== i))}
                onMove={(dir) => setOfferings((arr) => { const n = [...arr]; const k = i + dir; if (k < 0 || k >= n.length) return arr; [n[i], n[k]] = [n[k], n[i]]; return n; })}
              />
            ))}
          </div>
          <button onClick={() => setOfferings((arr) => [...arr, newOffering()])} className="mt-4 inline-flex items-center gap-1.5 border border-forest text-forest px-4 py-2 rounded-full text-sm font-semibold hover:bg-forest hover:text-bone transition-colors" data-testid="add-offering-btn">
            <Plus size={15} /> Add {meta.singular.toLowerCase()}
          </button>
        </Section>

        {partner?.partner_type === "state" && (
          <>
            <Section title="Conservation projects" icon={Leaf} testid="section-conservation">
              <p className="text-sm text-charcoal/60 -mt-2 mb-3">Highlight the programs and initiatives your agency runs.</p>
              <div className="space-y-3" data-testid="conservation-list">
                {conservation.map((c, i) => (
                  <div key={c.id || i} className="border border-saddle/20 bg-white rounded-lg p-3 space-y-2" data-testid={`conservation-item-${i}`}>
                    <div className="flex gap-2">
                      <input value={c.title || ""} onChange={(e) => setConservation((a) => a.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Project title" className="flex-1 border border-saddle/25 px-3 py-2 rounded-lg text-charcoal font-semibold text-sm" data-testid={`conservation-title-${i}`} />
                      <input value={c.status || ""} onChange={(e) => setConservation((a) => a.map((x, j) => j === i ? { ...x, status: e.target.value } : x))} placeholder="Status" className="w-28 border border-saddle/25 px-3 py-2 rounded-lg text-charcoal text-sm" data-testid={`conservation-status-${i}`} />
                      <button onClick={() => setConservation((a) => a.filter((_, j) => j !== i))} className="text-charcoal/40 hover:text-red-600 p-2" data-testid={`conservation-remove-${i}`}><Trash2 size={15} /></button>
                    </div>
                    <textarea value={c.description || ""} onChange={(e) => setConservation((a) => a.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} rows={2} placeholder="Description" className="w-full border border-saddle/25 px-3 py-2 rounded-lg text-charcoal text-sm" data-testid={`conservation-desc-${i}`} />
                  </div>
                ))}
              </div>
              <button onClick={() => setConservation((a) => [...a, { id: Math.random().toString(36).slice(2, 10), title: "", description: "", status: "" }])} className="mt-3 inline-flex items-center gap-1.5 border border-forest text-forest px-4 py-2 rounded-full text-sm font-semibold hover:bg-forest hover:text-bone transition-colors" data-testid="add-conservation-btn"><Plus size={15} /> Add project</button>
            </Section>

            <Section title="Resource center" icon={LinkIcon} testid="section-resources">
              <p className="text-sm text-charcoal/60 -mt-2 mb-3">Link hunters to regulations, applications, maps and more.</p>
              <div className="space-y-2" data-testid="resources-list">
                {resources.map((r, i) => (
                  <div key={r.id || i} className="flex gap-2 border border-saddle/20 bg-white rounded-lg p-3" data-testid={`resource-item-${i}`}>
                    <input value={r.title || ""} onChange={(e) => setResources((a) => a.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Title" className="flex-1 border border-saddle/25 px-3 py-2 rounded-lg text-charcoal text-sm" data-testid={`resource-title-${i}`} />
                    <input value={r.url || ""} onChange={(e) => setResources((a) => a.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className="flex-1 border border-saddle/25 px-3 py-2 rounded-lg text-charcoal text-sm" data-testid={`resource-url-${i}`} />
                    <button onClick={() => setResources((a) => a.filter((_, j) => j !== i))} className="text-charcoal/40 hover:text-red-600 p-2" data-testid={`resource-remove-${i}`}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => setResources((a) => [...a, { id: Math.random().toString(36).slice(2, 10), title: "", url: "" }])} className="mt-3 inline-flex items-center gap-1.5 border border-forest text-forest px-4 py-2 rounded-full text-sm font-semibold hover:bg-forest hover:text-bone transition-colors" data-testid="add-resource-btn"><Plus size={15} /> Add resource</button>
            </Section>
          </>
        )}

        {/* Contact & CTA */}
        <Section title="Contact, links & call-to-action" testid="section-contact">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Website" v={form.website} on={set("website")} testid="edit-website" />
            <Field label="Contact email" v={form.email} on={set("email")} testid="edit-email" />
            <Field label="Phone" v={form.phone} on={set("phone")} testid="edit-phone" />
            <Field label="States served (comma-separated)" v={form.states_served} on={set("states_served")} testid="edit-states" />
            <Field label="Button label" v={form.cta_label} on={set("cta_label")} testid="edit-cta-label" />
            <Field label="Button link" v={form.cta_url} on={set("cta_url")} testid="edit-cta-url" />
          </div>
          <div className="grid sm:grid-cols-4 gap-3 mt-3">
            <Field label="Instagram" v={form.instagram} on={set("instagram")} testid="edit-instagram" />
            <Field label="Facebook" v={form.facebook} on={set("facebook")} testid="edit-facebook" />
            <Field label="YouTube" v={form.youtube} on={set("youtube")} testid="edit-youtube" />
            <Field label="X / Twitter" v={form.x} on={set("x")} testid="edit-x" />
          </div>
        </Section>

        <div className="flex justify-end mt-8">
          <button onClick={saveProfile} disabled={saving} className="inline-flex items-center gap-1.5 bg-forest text-bone px-6 py-3 rounded-full font-semibold disabled:opacity-50 hover:bg-forest-2 transition-colors" data-testid="editor-save-btn-bottom">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
          </button>
        </div>
      </motion.div>
    </Shell>
  );
}

function OfferingCard({ o, i, meta, onChange, onRemove, onMove }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const res = await api.partnerUploadOfferingImage(file); onChange({ image: res.url }); }
    catch { toast.error("Image upload failed"); }
    setUploading(false);
  };
  return (
    <div className="border border-saddle/20 bg-white rounded-xl p-4" data-testid={`offering-${i}`}>
      <div className="flex items-start gap-4">
        <label className="w-24 h-24 shrink-0 rounded-lg border border-dashed border-saddle/40 overflow-hidden grid place-items-center cursor-pointer hover:border-forest" data-testid={`offering-image-${i}`}>
          {o.image ? <img src={o.image} alt="" className="w-full h-full object-cover" /> : (uploading ? <Loader2 size={18} className="animate-spin text-saddle" /> : <ImagePlus size={18} className="text-saddle" />)}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <div className="flex-1 space-y-2">
          <input value={o.title} onChange={(e) => onChange({ title: e.target.value })} placeholder={`${meta.singular} name`} className="w-full border border-saddle/25 bg-white px-3 py-2 rounded-lg text-charcoal font-semibold" data-testid={`offering-title-${i}`} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <input value={o.price} onChange={(e) => onChange({ price: e.target.value })} placeholder='Price (e.g. "From $2,500")' className="border border-saddle/25 bg-white px-3 py-1.5 rounded-lg text-sm text-charcoal" data-testid={`offering-price-${i}`} />
            {meta.showHunt && <input value={o.species} onChange={(e) => onChange({ species: e.target.value })} placeholder="Species" className="border border-saddle/25 bg-white px-3 py-1.5 rounded-lg text-sm text-charcoal" data-testid={`offering-species-${i}`} />}
            {meta.showHunt && <input value={o.duration} onChange={(e) => onChange({ duration: e.target.value })} placeholder="Duration (e.g. 5 days)" className="border border-saddle/25 bg-white px-3 py-1.5 rounded-lg text-sm text-charcoal" data-testid={`offering-duration-${i}`} />}
          </div>
          <textarea value={o.description} onChange={(e) => onChange({ description: e.target.value })} rows={2} placeholder="Description" className="w-full border border-saddle/25 bg-white px-3 py-2 rounded-lg text-sm text-charcoal" data-testid={`offering-desc-${i}`} />
          <input value={o.link} onChange={(e) => onChange({ link: e.target.value })} placeholder="Link (optional)" className="w-full border border-saddle/25 bg-white px-3 py-1.5 rounded-lg text-sm text-charcoal" data-testid={`offering-link-${i}`} />
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => onMove(-1)} className="p-1.5 text-charcoal/50 hover:text-forest" data-testid={`offering-up-${i}`}><ArrowUp size={15} /></button>
          <button onClick={() => onMove(1)} className="p-1.5 text-charcoal/50 hover:text-forest" data-testid={`offering-down-${i}`}><ArrowDown size={15} /></button>
          <button onClick={onRemove} className="p-1.5 text-charcoal/50 hover:text-red-600" data-testid={`offering-remove-${i}`}><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="mt-8 border border-saddle/15 bg-white rounded-2xl p-5 sm:p-6" data-testid={testid}>
      <h2 className="font-serif text-xl text-forest flex items-center gap-2 mb-4">{Icon && <Icon size={18} className="text-blaze" />}{title}</h2>
      {children}
    </section>
  );
}
function Field({ label, v, on, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] font-bold uppercase tracking-wide text-saddle">{label}</label>
      <input value={v} onChange={on} className="w-full mt-1.5 border border-saddle/25 bg-white px-4 py-2.5 rounded-lg text-charcoal" data-testid={testid} />
    </div>
  );
}
function TextArea({ label, v, on, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] font-bold uppercase tracking-wide text-saddle">{label}</label>
      <textarea value={v} onChange={on} rows={4} className="w-full mt-1.5 border border-saddle/25 bg-white px-4 py-3 rounded-lg text-charcoal" data-testid={testid} />
    </div>
  );
}
function Center({ children }) { return <div className="py-32 grid place-items-center">{children}</div>; }
function Shell({ children }) {
  return (
    <div className="bg-paper min-h-screen" data-testid="partner-editor">
      <SiteHeader />
      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">{children}</main>
      <Footer />
    </div>
  );
}
